import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Container, 
  Card, 
  CardContent, 
  Typography, 
  TextField, 
  Button, 
  Link, 
  Alert, 
  InputAdornment, 
  IconButton,
  Divider,
  useTheme,
  useMediaQuery
} from '@mui/material';
import { 
  Email, 
  Lock, 
  Visibility, 
  VisibilityOff, 
  ArrowForward,
  PersonAdd
} from '@mui/icons-material';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useMutation } from "react-query";
import apiClient from '../../request/http-common';
import { setUser } from './userSlice';
import { setAuthStatus, setToken } from '../../request/Auth/authSlice';

const Login = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const appMode = useSelector((state) => state.mode.mode);
  const redirectTo = useSelector((state) => state.redirect.redirect);

  const [registerForm, setRegisterForm] = useState({
    email: '',
    password: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');

  const onChange = (e) =>
    setRegisterForm({ ...registerForm, [e.target.name]: e.target.value });

  const { isLoading: isSendingRequest, mutate: postLogin } = useMutation(
    async () => {
      return await apiClient.post(`/api/login`, {
        email: registerForm.email,
        password: registerForm.password,
      });
    },
    {
      onSuccess: (res) => {
        localStorage.setItem('auth-token', res.data.access_token);
        dispatch(setUser(res.data.user));
        dispatch(setAuthStatus('authenticated'));
        dispatch(setToken(res.data.access_token));
        localStorage.setItem('notification', JSON.stringify({message : `${res.data.message}`, 'type': 'success' }));
        const event2 = new Event('processed');
        window.dispatchEvent(event2);
        const event = new Event('newMessage');
        window.dispatchEvent(event);

        if (redirectTo !== '') {
          navigate(redirectTo);
        } else {
          window.location.href = '/';
        }
      },
      onError: (err) => {   
        let myerror = err.response?.data || err;         
        setError(myerror.message) 
        const event2 = new Event('processed');
        window.dispatchEvent(event2);      
      },
    }
  );

  const onSubmit = async() => {
    if (!registerForm.email || !registerForm.password) {
      return;
    }
    
    const event3 = new Event('processing');
    window.dispatchEvent(event3);
    postLogin();
  };

  return (
    <Box 
      sx={{ 
        minHeight: '100vh',
        background: appMode === 'dark' 
          ? 'linear-gradient(135deg, #0f0f23 0%, #1a1a2e 50%, #16213e 100%)'
          : 'linear-gradient(135deg, #0050b4 0%, #0070fc 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: { xs: '16px', sm: '24px', md: '32px' }
      }}
    >
      <Container maxWidth="sm" sx={{ px: { xs: 1, sm: 2 } }}>
        <Card 
          sx={{ 
            maxWidth: '480px', 
            mx: 'auto',
            borderRadius: '20px',
            boxShadow: appMode === 'dark' 
              ? '0 20px 40px rgba(0, 0, 0, 0.4)'
              : '0 20px 40px rgba(0, 0, 0, 0.1)',
            background: appMode === 'dark' 
              ? 'rgba(255, 255, 255, 0.05)'
              : 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(20px)',
            border: appMode === 'dark' 
              ? '1px solid rgba(255, 255, 255, 0.1)'
              : '1px solid rgba(255, 255, 255, 0.2)'
          }}
        >
          <CardContent sx={{ p: { xs: 4, sm: 5, md: 6 } }}>
            
            {/* Logo Section */}
            <Box sx={{ textAlign: 'center', mb: 5 }}>
              <img  
                src={appMode === 'dark' 
                  ? '/logos/youngtrade-logo-white.png' 
                  : '/logos/youngtrade-logo-dark.png'
                } 
                style={{
                  height: isMobile ? '70px' : '80px', 
                  marginBottom: '24px',
                  filter: appMode === 'dark' ? 'brightness(1.2)' : 'none'
                }} 
                alt="Young Trade Logo" 
              />
              <Typography 
                variant="h4" 
                sx={{ 
                  fontWeight: 800, 
                  background: 'linear-gradient(135deg, #0050b4 0%, #0070fc 100%)',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  mb: 2,
                  fontSize: { xs: '1.75rem', sm: '2.125rem' }
                }}
              >
                Welcome Back
              </Typography>
              <Typography 
                variant="body1" 
                sx={{ 
                  color: appMode === 'dark' ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.6)',
                  fontSize: '16px',
                  fontWeight: 400
                }}
              >
                Sign in to your account to continue trading
              </Typography>
            </Box>

            {/* Error Alert */}
            {error && (
              <Alert 
                severity="error" 
                sx={{ 
                  mb: 4, 
                  borderRadius: '12px',
                  background: appMode === 'dark' ? 'rgba(244, 67, 54, 0.1)' : 'rgba(244, 67, 54, 0.05)',
                  border: '1px solid rgba(244, 67, 54, 0.2)',
                  '& .MuiAlert-icon': { fontSize: '20px' }
                }}
              >
                {error}
              </Alert>
            )}

            {/* Form Fields */}
            <Box sx={{ mb: 4 }}>
              <TextField
                fullWidth
                label="Email Address"
                name="email"
                type="email"
                value={registerForm.email}
                onChange={onChange}
                variant="outlined"
                sx={{
                  mb: 3,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '12px',
                    background: appMode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.02)',
                    '& fieldset': {
                      borderColor: appMode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
                    },
                    '&:hover fieldset': {
                      borderColor: '#0050b4',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#0050b4',
                    },
                  },
                  '& .MuiInputLabel-root': {
                    color: appMode === 'dark' ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.6)',
                    '&.Mui-focused': {
                      color: '#0050b4',
                    },
                  },
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Email sx={{ 
                        color: appMode === 'dark' ? 'rgba(255, 255, 255, 0.5)' : 'rgba(0, 0, 0, 0.4)',
                        fontSize: '20px' 
                      }} />
                    </InputAdornment>
                  ),
                }}
              />

              <TextField
                fullWidth
                label="Password"
                name="password"
                type={showPassword ? 'text' : 'password'}
                value={registerForm.password}
                onChange={onChange}
                variant="outlined"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '12px',
                    background: appMode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.02)',
                    '& fieldset': {
                      borderColor: appMode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
                    },
                    '&:hover fieldset': {
                      borderColor: '#0050b4',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#0050b4',
                    },
                  },
                  '& .MuiInputLabel-root': {
                    color: appMode === 'dark' ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.6)',
                    '&.Mui-focused': {
                      color: '#0050b4',
                    },
                  },
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Lock sx={{ 
                        color: appMode === 'dark' ? 'rgba(255, 255, 255, 0.5)' : 'rgba(0, 0, 0, 0.4)',
                        fontSize: '20px' 
                      }} />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                        sx={{ 
                          color: appMode === 'dark' ? 'rgba(255, 255, 255, 0.5)' : 'rgba(0, 0, 0, 0.4)',
                          '&:hover': {
                            color: '#0050b4'
                          }
                        }}
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Box>

            {/* Forgot Password Link */}
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'flex-end', 
              mb: 4
            }}>
              <Link 
                component={RouterLink} 
                to='/forgot' 
                underline="none"
                sx={{ 
                  color: '#0050b4',
                  fontWeight: 600,
                  fontSize: '14px',
                  transition: 'all 0.2s ease',
                  '&:hover': { 
                    color: '#E8851A',
                    textDecoration: 'underline' 
                  }
                }}
              >
                Forgot Password?
              </Link>
            </Box>

            {/* Sign In Button */}
            <Button
              onClick={onSubmit}
              fullWidth
              size="large"
              variant="contained"
              disabled={isSendingRequest || !registerForm.email || !registerForm.password}
              endIcon={isSendingRequest ? null : <ArrowForward />}
              sx={{ 
                height: '56px',
                fontSize: '16px',
                fontWeight: 700,
                borderRadius: '12px',
                background: 'linear-gradient(135deg, #0050b4 0%, #0070fc 100%)',
                boxShadow: '0 8px 24px rgba(0, 80, 180, 0.3)',
                transition: 'all 0.3s ease',
                textTransform: 'none',
                '&:hover': {
                  background: 'linear-gradient(135deg, #003d8c 0%, #0050b4 100%)',
                  boxShadow: '0 12px 32px rgba(0, 80, 180, 0.4)',
                  transform: 'translateY(-2px)'
                },
                '&:disabled': {
                  background: appMode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
                  color: appMode === 'dark' ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.3)',
                  boxShadow: 'none',
                  transform: 'none'
                }
              }}
            >
              {isSendingRequest ? 'Signing In...' : 'Sign In'}
            </Button>

            {/* Divider */}
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              my: 4,
              '&::before, &::after': {
                content: '""',
                flex: 1,
                height: '1px',
                background: appMode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'
              }
            }}>
              <Typography 
                variant="body2" 
                sx={{ 
                  px: 2,
                  color: appMode === 'dark' ? 'rgba(255, 255, 255, 0.5)' : 'rgba(0, 0, 0, 0.5)',
                  fontSize: '14px',
                  fontWeight: 500
                }}
              >
                Don't have an account?
              </Typography>
            </Box>

            {/* Create Account Button */}
            <Button
              component={RouterLink}
              to='/register'
              fullWidth
              size="large"
              variant="outlined"
              startIcon={<PersonAdd />}
              sx={{ 
                height: '56px',
                fontSize: '16px',
                fontWeight: 600,
                borderRadius: '12px',
                border: '2px solid #0050b4',
                color: '#0050b4',
                background: 'transparent',
                transition: 'all 0.3s ease',
                textTransform: 'none',
                '&:hover': {
                  background: 'rgba(0, 80, 180, 0.1)',
                  borderColor: '#0070fc',
                  color: '#0070fc',
                  transform: 'translateY(-1px)'
                }
              }}
            >
              Create Account
            </Button>

            {/* Footer */}
            <Box sx={{ textAlign: 'center', mt: 5 }}>
              <Typography 
                variant="body2" 
                sx={{ 
                  color: appMode === 'dark' ? 'rgba(255, 255, 255, 0.5)' : 'rgba(0, 0, 0, 0.5)',
                  fontSize: '13px',
                  lineHeight: 1.5
                }}
              >
                By signing in, you agree to our{' '}
                <Link 
                  href="#" 
                  sx={{ 
                    color: '#0050b4', 
                    textDecoration: 'none',
                    '&:hover': { textDecoration: 'underline' }
                  }}
                >
                  Terms of Service
                </Link>
                {' '}and{' '}
                <Link 
                  href="#" 
                  sx={{ 
                    color: '#0050b4', 
                    textDecoration: 'none',
                    '&:hover': { textDecoration: 'underline' }
                  }}
                >
                  Privacy Policy
                </Link>
              </Typography>
            </Box>
          </CardContent>
        </Card>
      </Container>
    </Box>
  );
};

export default Login;
